import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import type { LoanLibraryLoanItem, LoanLibraryLoanFilterLists, LoanLibraryLoanApiItem } from './loanLibraryLoan.types';
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {loanLibraryLoanApi} from "../../../Library/LoanLibrary/LoanLibraryLoan/loanLibraryLoanApi";
import {SYSTEM_TYPES} from "../../../Library/libraryConstants";
import {getSystemTypeTextByValue} from "../../../Library/libraryUtility";
import {PageTitle} from "../../../../Components/Landmarks";
import {LoanLibraryLoanSearchFormView} from "./LoanLibraryLoanSearchFormView";
import {LayoutFullWidth} from "../../../../Components/Layout";
import {LoanLibraryLoanTable} from "./LoanLibraryLoanTable";
import {allow, policyEvents} from "../../../../Components/Authorize";
import {loanLibraryLoanItemModel, updateLoanLibraryLoanItemModel} from "./loanLibraryLoanFactory";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {Button, ButtonLinkStyle} from "../../../../Components/Inputs";
import {getChangedLoanLibraryLoanItemsForUpdate} from "./loanLibraryLoanUtility";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";
import {openInNewTab} from "../../../../Utilities/htmlUtilities";
import {locations} from "../../../../Utilities/Location";
import {loanLibraryLoanReportApi} from "./loanLibraryLoanApi";

export const LoanLibraryLoanManagementContainer = ({
                                                       handleApiCall,
                                                       handleSetLoanLibraryLoanSearchCriteria,
                                                       isLoading,
                                                       loanLibraryLoanSearchCriteria,
                                                       districts,
                                                   }: ContextProps) => {

    const [loanLibraryLoanItems, setLoanLibraryLoanItems] = useState<LoanLibraryLoanItem[]>([]);
    const [originalLoanLibraryLoanItems, setOriginalLoanLibraryLoanItems] = useState<LoanLibraryLoanItem[]>([]);
    const [loanLibraryLoanSearchDropDowns, setLoanLibraryLoanSearchDropDowns] = useState<LoanLibraryLoanFilterLists | null>(null);
    const [totalRecords, setTotalRecords] = useState(0);

    const canManageLoanLibraryLoans = allow(policyEvents.MANAGE_LOAN_LIBRARY_LOANS);

    const loadLoanLibraryLoanItemsResult = (result: { items: LoanLibraryLoanApiItem[]; totalCount: number }) => {
        const loanLibraryLoanItems = result.items.map((loan: LoanLibraryLoanApiItem) => loanLibraryLoanItemModel(loan));
        setLoanLibraryLoanItems(loanLibraryLoanItems);
        setOriginalLoanLibraryLoanItems(loanLibraryLoanItems);
        setTotalRecords(result.totalCount);
    };

    const handleGetLoanLibraryLoanItemsReadyForProcessing = () => {
        handleApiCall(() => loanLibraryLoanApi.getLoansReadyForProcessing({
            ...loanLibraryLoanSearchCriteria,
        }), loadLoanLibraryLoanItemsResult);
    }

    const handleGetLoanLibraryLoanSearchDropDowns = () => {
        handleApiCall(() => loanLibraryLoanApi.getDropdownListsForLoanLibraryLoanSearch(), (results: LoanLibraryLoanFilterLists) => setLoanLibraryLoanSearchDropDowns(results));
    }


    const updateLoanLibraryLoanItemInState = (loanLibraryLoanId: string, loanLibraryLoanStatus: string | number, notes: string | null, numberOfExtensionDays: number | null) => {

        setLoanLibraryLoanItems((currentLoanLibraryLoanItems) =>
            currentLoanLibraryLoanItems.map((item: LoanLibraryLoanItem) =>
                item.loanLibraryLoanId === loanLibraryLoanId
                    ? {
                        ...item,
                        loanLibraryLoanStatus,
                        loanLibraryLoanNotes: notes ? notes : item.loanLibraryLoanNotes,
                        numberOfExtensionDays: numberOfExtensionDays != null ? numberOfExtensionDays : item.numberOfExtensionDays,
                    }
                    : item
            )
        );
    };

    const syncOriginalLoanLibraryLoanItemAfterSave = (updatedLoanLibraryLoanItem: Record<string, unknown> | Record<string, unknown>[]) => {
        const responseItem = Array.isArray(updatedLoanLibraryLoanItem)
            ? updatedLoanLibraryLoanItem[0]
            : updatedLoanLibraryLoanItem;

        const transformedItem = loanLibraryLoanItemModel(responseItem);

        setLoanLibraryLoanItems((currentLoanLibraryLoanItems) =>
            currentLoanLibraryLoanItems.map((item: LoanLibraryLoanItem) =>
                item.loanLibraryLoanId === transformedItem.loanLibraryLoanId
                    ? {
                        ...item,
                        loanLibraryLoanStatus: transformedItem.loanLibraryLoanStatus,
                        loanLibraryLoanNotes: transformedItem.loanLibraryLoanNotes || item.loanLibraryLoanNotes,
                        loanItemNotes: transformedItem.loanItemNotes || item.loanItemNotes,
                        numberOfExtensionDays: transformedItem.numberOfExtensionDays != null ? transformedItem.numberOfExtensionDays : item.numberOfExtensionDays,
                        dueDate: transformedItem.dueDate || item.dueDate,
                        dueDateFormatted: transformedItem.dueDateFormatted || item.dueDateFormatted,
                    }
                    : item
            )
        );

        setOriginalLoanLibraryLoanItems((currentLoanLibraryLoanItems) =>
            currentLoanLibraryLoanItems.map((item: LoanLibraryLoanItem) =>
                item.loanLibraryLoanId === transformedItem.loanLibraryLoanId
                    ? {
                        ...item,
                        loanLibraryLoanStatus: transformedItem.loanLibraryLoanStatus,
                        loanLibraryLoanNotes: transformedItem.loanLibraryLoanNotes || item.loanLibraryLoanNotes,
                        numberOfExtensionDays: transformedItem.numberOfExtensionDays != null ? transformedItem.numberOfExtensionDays : item.numberOfExtensionDays,
                        dueDate: transformedItem.dueDate || item.dueDate,
                        dueDateFormatted: transformedItem.dueDateFormatted || item.dueDateFormatted,
                    }
                    : item
            )
        );
    };

    const handleSaveUpdatedLoanLibraryLoanItem = (updatedLoanLibraryLoanItem: LoanLibraryLoanItem) => {
        updateLoanLibraryLoanItemInState(updatedLoanLibraryLoanItem.loanLibraryLoanId, updatedLoanLibraryLoanItem.loanLibraryLoanStatus, updatedLoanLibraryLoanItem.loanLibraryLoanNotes, updatedLoanLibraryLoanItem.numberOfExtensionDays);
        const loanLibraryLoanItemForSaving = [
            updateLoanLibraryLoanItemModel({
                ...updatedLoanLibraryLoanItem,
            })
        ];
        handleApiCall(() => loanLibraryLoanApi.updateLoans(loanLibraryLoanItemForSaving), syncOriginalLoanLibraryLoanItemAfterSave);
    }

    const handleSaveUpdatedLoanLibraryLoanItems = () => {

        const updatedLoanLibraryLoanItems = getChangedLoanLibraryLoanItemsForUpdate(loanLibraryLoanItems, originalLoanLibraryLoanItems);

        if (updatedLoanLibraryLoanItems.length === 0) {
            alert("No changes to save.");
            return;
        }

        if (confirm(`Are you sure you want to save ${updatedLoanLibraryLoanItems.length} updated Loan Library loan item(s)?`)) {
            handleApiCall(() => loanLibraryLoanApi.updateLoans(updatedLoanLibraryLoanItems), handleGetLoanLibraryLoanItemsReadyForProcessing);
        }
    };

    const handleReturnLoan = (inventoryItemId: string) => {
        if (confirm("Are you sure you want to return this item? This action cannot be undone.")) {
            handleApiCall(() => loanLibraryLoanApi.returnItem({loanLibraryInventoryItemId: inventoryItemId}), handleGetLoanLibraryLoanItemsReadyForProcessing);
        }
    };

    const handleExportLoansClick = () => {
        if (isArrayNullOrEmpty(loanLibraryLoanItems)) {
            alert("Search criteria has no results. Adjust your search and try again.")
        }
        else
            handleApiCall(() => loanLibraryLoanReportApi.getLoanLibraryLoansReport(loanLibraryLoanSearchCriteria));
    };

    const handleLoanLibraryPackingList = (loanLibraryLoanId: string) => {
        openInNewTab(locations.LOAN_LIBRARY_PACKING_LIST.getLink(loanLibraryLoanId).to);
    }


    useEffect(() => {

        if(!loanLibraryLoanSearchDropDowns)
            handleGetLoanLibraryLoanSearchDropDowns();

        handleGetLoanLibraryLoanItemsReadyForProcessing();

    }, [loanLibraryLoanSearchCriteria]);


    if (isNullOrUndefined(loanLibraryLoanSearchDropDowns)) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.LoanLibrary.toString())} Loan Management`}/>

        <LoanLibraryLoanSearchFormView
            districts={districts}
            loanLibraryLoanSearchCriteria={loanLibraryLoanSearchCriteria}
            loanLibraryLoanSearchDropDowns={loanLibraryLoanSearchDropDowns}
            handleSetLoanLibraryLoanSearchCriteria={handleSetLoanLibraryLoanSearchCriteria}
            isLoading={isLoading}
        />

        <LayoutFullWidth className={`catalog`}>
            <header>
                <h2>Search Results</h2>
            </header>
            {
                loanLibraryLoanItems &&
                <>
                    <GridContainer isReverseOrder className={`form__header`} hasMarginX>
                        <GridCell
                            medium_columns={gridConstants.column.THREE}
                            small_columns={gridConstants.column.SIX}>
                            <Button
                                disabled={isLoading}
                                isPrimary
                                label="Save Updates"
                                name="btnSave"
                                onClick={handleSaveUpdatedLoanLibraryLoanItems}
                            />
                        </GridCell>
                        <GridCell
                            medium_columns={gridConstants.column.ONE}>
                            <ButtonLinkStyle
                                isCollapsed
                                label={`Export Loan Library Loans`}
                                name="btnExportLoans"
                                onClick={handleExportLoansClick}
                            />
                        </GridCell>
                    </GridContainer>

                    <LoanLibraryLoanTable
                        loanLibraryLoanItems={loanLibraryLoanItems}
                        loanLibraryLoanSearchCriteria={loanLibraryLoanSearchCriteria}
                        canManageLoanLibraryLoans={canManageLoanLibraryLoans}
                        handleSetLoanLibraryLoanSearchCriteria={handleSetLoanLibraryLoanSearchCriteria}
                        handleSaveUpdatedLoanLibraryLoanItem={handleSaveUpdatedLoanLibraryLoanItem}
                        updateLoanLibraryLoanItemInState={updateLoanLibraryLoanItemInState}
                        handleReturnLoan={handleReturnLoan}
                        handleLoanLibraryPackingList={handleLoanLibraryPackingList}
                        totalRecords={totalRecords}
                    />
                </>
            }
        </LayoutFullWidth>

    </>;
};

